import { CheckBox } from '@isdd/idsk-ui-kit/checkbox/CheckBox'
import { Button, ButtonLink, Filter, TextHeading } from '@isdd/idsk-ui-kit/index'
import { PaginatorWrapper } from '@isdd/idsk-ui-kit/paginatorWrapper/PaginatorWrapper'
import { Table } from '@isdd/idsk-ui-kit/table/Table'
import { CHECKBOX_CELL } from '@isdd/idsk-ui-kit/table/constants'
import { Tooltip } from '@isdd/idsk-ui-kit/tooltip/Tooltip'
import { IFilter, Pagination } from '@isdd/idsk-ui-kit/types'
import { DMS_DOWNLOAD_FILE, KRIT, KRIT_HODNOTENIE } from '@isdd/metais-common/api/constants'
import { ConfigurationItemUi, getReadCiNeighboursQueryKey } from '@isdd/metais-common/api/generated/cmdb-swagger'
import { useGetProtokolPdfHook } from '@isdd/metais-common/api/generated/pdf-creator'
import { ApiError, WikiDocument, useGetDocumentHook, useUploadProjectDocument1Hook } from '@isdd/metais-common/api/generated/wiki-swagger'
import { downloadBlobAsFile } from '@isdd/metais-common/componentHelpers/download/downloadHelper'
import styles from '@isdd/metais-common/components/actions-over-table/single-actions-popup/file-history/styles.module.scss'
import { Spacer } from '@isdd/metais-common/components/spacer/Spacer'
import { DOCUMENTS, ENTITY_KRIS, INVALIDATED, ReponseErrorCodeEnum } from '@isdd/metais-common/constants'
import { useAuth } from '@isdd/metais-common/contexts/auth/authContext'
import { IBulkActionResult, useBulkAction } from '@isdd/metais-common/hooks/useBulkAction'
import { useGetFileSize } from '@isdd/metais-common/hooks/useGetFileSize'
import { useScroll } from '@isdd/metais-common/hooks/useScroll'
import {
    ActionsOverTable,
    BulkPopup,
    DeleteFileBulkModal,
    FileHistoryModal,
    InvalidateBulkModal,
    MutationFeedback,
    ProjectUploadFileModal,
    QueryFeedback,
    ReInvalidateBulkModal,
    UpdateFileModal,
} from '@isdd/metais-common/index'
import { useQueryClient } from '@tanstack/react-query'
import { CellContext, ColumnDef } from '@tanstack/react-table'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { RowSelection } from '@isdd/metais-common/hooks/useRowSelection'
import { useUserPreferences } from '@isdd/metais-common/contexts/userPreferences/userPreferencesContext'

import { TableCols, defaultFilter } from '@/components/containers/DocumentListContainer'
import { downloadFile, isDocumentUpdatable, isDocumentsUpdatable } from '@/components/views/documents/utils'

interface KRISDocumentsTable {
    ciData?: ConfigurationItemUi
    refetch: () => void
    data?: TableCols[]
    protocols?: ConfigurationItemUi[]
    isLoading: boolean
    isError: boolean
    additionalColumns?: Array<ColumnDef<TableCols>>
    pagination: Pagination
    handleFilterChange: (filter: IFilter) => void
    namesData?: { login: string; fullName: string }[]
    rowSelection: RowSelection<TableCols>
    xWikiDocData?: WikiDocument
}

export const KRISDocumentsTab: React.FC<KRISDocumentsTable> = ({
    data,
    additionalColumns,
    isLoading,
    isError,
    pagination,
    handleFilterChange,
    refetch,
    namesData,
    rowSelection,
    ciData,
    protocols,
    xWikiDocData,
}) => {
    const { t, i18n } = useTranslation()
    const {
        state: { user },
    } = useAuth()
    const { currentPreferences } = useUserPreferences()
    const isUserAdmin = user?.roles.includes('R_ADMIN')
    const isUserLogged = user !== null
    const isInvalidated = ciData?.metaAttributes?.state === INVALIDATED
    const canManageOpinionDoc = user?.roles.some((role: string) => role === 'KRIS_SCHVAL') ?? false
    const additionalColumnsNullsafe = additionalColumns ?? []
    const { errorMessage, isBulkLoading, handleInvalidate, handleReInvalidate, handleDeleteFile, handleUpdateFile } = useBulkAction()
    const xWikiDoc = useGetDocumentHook()

    const xWikiDocUpload = useUploadProjectDocument1Hook()
    const [showInvalidate, setShowInvalidate] = useState<boolean>(false)
    const [showReInvalidate, setShowReInvalidate] = useState<boolean>(false)
    const [showDeleteFile, setShowDeleteFile] = useState<boolean>(false)
    const [bulkActionResult, setBulkActionResult] = useState<IBulkActionResult>()
    const [openAddModal, setOpenAddModal] = useState<ConfigurationItemUi>()
    const [invalidateSingle, setInvalidateSingle] = useState<ConfigurationItemUi>()
    const [deleteSingle, setDeleteSingle] = useState<ConfigurationItemUi>()
    const [updateFile, setUpdateFile] = useState<ConfigurationItemUi>()
    const [singleItemHistory, setSingleItemHistory] = useState<ConfigurationItemUi>()
    const queryClient = useQueryClient()
    const queryKey = getReadCiNeighboursQueryKey(ciData?.uuid ?? '', {})
    const downloadProtocolRequest = useGetProtokolPdfHook()
    const [successfullyAdded, setSuccessfullyAdded] = useState<string[]>([])
    const [isLocalLoading, setIsLocalLoading] = useState(false)
    const [documents, setDocuments] = useState<TableCols[] | undefined>(data)

    useEffect(() => {
        if (data) {
            setDocuments(data)
        }
    }, [data])

    const downloadProtocol = async (ci: ConfigurationItemUi) => {
        const pdf = (await downloadProtocolRequest({ krisUuid: ciData?.uuid, protokolUuid: ci.uuid })) as unknown as Blob
        downloadBlobAsFile(new Blob([pdf]), ci?.attributes?.Gen_Profil_nazov + '.pdf', false)
    }

    const [filesSizes, setFilesSizes] = useState<{ uuid: string; size: string; extension: string; metaComponent: string }[]>([])
    const { loadData } = useGetFileSize()

    const loadSizeInfo = useCallback(async () => {
        const sizes = await loadData(data?.filter((d) => !!d.configurationItem?.uuid).map((d) => d?.configurationItem?.uuid ?? '') ?? [])

        setFilesSizes(sizes)
    }, [data, loadData])

    useEffect(() => {
        if ((data?.length != 0 && filesSizes?.length == 0) || data?.length != filesSizes.length) {
            loadSizeInfo()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, filesSizes?.length])

    const handleCloseBulkModal = (actionResult: IBulkActionResult, closeFunction: () => void) => {
        closeFunction()
        queryClient.invalidateQueries({ queryKey: [queryKey[0]] })
        refetch()
        setBulkActionResult(actionResult)
        rowSelection.clearSelectedRows()
    }

    useEffect(() => {
        setDocuments(documents?.filter((d) => d.configurationItem?.type != KRIT_HODNOTENIE))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language])

    useEffect(() => {
        if (
            documents &&
            xWikiDocData &&
            !documents?.map((d) => d.configurationItem?.attributes?.Profil_Dokument_Wiki_typ_dokumentu).includes(KRIT_HODNOTENIE) &&
            !documents?.find((d) => d.configurationItem?.type == KRIT_HODNOTENIE) &&
            canManageOpinionDoc
        ) {
            setDocuments([
                {
                    relationship: {},
                    configurationItem: {
                        uuid: undefined,
                        type: KRIT_HODNOTENIE,
                        attributes: { Gen_Profil_nazov: t('docs.opinionName', { type: KRIT }) },
                    },
                },
                ...(data ?? []),
            ])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, xWikiDocData, documents, canManageOpinionDoc])

    const createAndOpenXWikiDoc = (rerenderDynamicContent = false) => {
        setIsLocalLoading(true)
        xWikiDoc(ENTITY_KRIS, ciData?.uuid ?? '', KRIT_HODNOTENIE, { createIfNotExists: true, rerenderDynamicContent })
            .then((doc) => doc.xwikiUrl && window.open(doc.xwikiUrl, '_blank'))
            .catch(() => setBulkActionResult({ isSuccess: false, isError: true }))
            .finally(() => setIsLocalLoading(false))
    }

    const uploadXWikiDoc = async (row: CellContext<TableCols, unknown>) => {
        if (ciData?.uuid && row.cell.row.original.configurationItem) {
            setIsLocalLoading(true)
            xWikiDocUpload(ENTITY_KRIS, ciData?.uuid, KRIT_HODNOTENIE)
                .then((resp) => {
                    if (resp) {
                        refetch()
                        setBulkActionResult({ isSuccess: true, isError: false, successMessage: t('bulkActions.addFile.success') })
                    }
                })
                .catch((err) => {
                    if (JSON.parse((err as ApiError).message ?? '').type == ReponseErrorCodeEnum.GNR404) {
                        setBulkActionResult({
                            isSuccess: false,
                            isError: true,
                            errorMessage: t('feedback.documentForProjectDoesntExists', {
                                docName: row.cell.row.original.configurationItem?.attributes?.Gen_Profil_nazov,
                                projectCode: ciData.attributes?.Gen_Profil_nazov,
                            }),
                        })
                    }
                })
                .finally(() => setIsLocalLoading(false))
        }
    }

    const resolveBulkActions = (
        row: CellContext<TableCols, unknown>,
        docMeta:
            | {
                  uuid: string
                  size: string
                  extension: string
                  metaComponent: string
              }
            | undefined,
    ) => {
        if (row.cell.row.original.configurationItem?.type == KRIT_HODNOTENIE && row.cell.row.original.configurationItem?.uuid == undefined) {
            return [
                <ButtonLink key={'buttonOpen'} label={t('actionOverTable.options.openDocument')} onClick={() => createAndOpenXWikiDoc()} inPopup />,
                // <ButtonLink
                //     key={'buttonUpdate'}
                //     label={t('actionOverTable.options.updateDocument')}
                //     onClick={() => createAndOpenXWikiDoc(true)}
                //     inPopup
                // />,
                <ButtonLink
                    key={'buttonUpload'}
                    label={t('actionOverTable.options.uploadDocument')}
                    onClick={async () => uploadXWikiDoc(row)}
                    inPopup
                />,
            ]
        } else if (row.cell.row.original.configurationItem?.attributes?.Profil_Dokument_Wiki_typ_dokumentu == KRIT_HODNOTENIE) {
            return [
                <ButtonLink
                    key={'buttonOpen'}
                    label={t('actionOverTable.options.openDocument')}
                    onClick={() => createAndOpenXWikiDoc()}
                    hidden={!canManageOpinionDoc}
                    inPopup
                />,
                // <ButtonLink
                //     key={'buttonUpdate'}
                //     label={t('actionOverTable.options.updateDocument')}
                //     onClick={() => createAndOpenXWikiDoc(true)}
                //     hidden={!canManageOpinionDoc}
                //     inPopup
                // />,
                <ButtonLink
                    key={'buttonDownload'}
                    inPopup
                    label={`${t('actionOverTable.options.download')} ${docMeta?.metaComponent ?? ''}`}
                    onClick={() => {
                        const item = data ? data[row.row.index] : {}
                        downloadFile(
                            `${DMS_DOWNLOAD_FILE}${item.configurationItem?.uuid}`,
                            item.configurationItem?.attributes?.Gen_Profil_nazov as string,
                        )
                    }}
                />,
                <ButtonLink
                    inPopup
                    key={'buttonInvalidate'}
                    label={t('actionOverTable.options.invalidate')}
                    disabled={!isUserLogged}
                    onClick={() => {
                        if (data !== undefined) {
                            const item = data[row.row.index]
                            if (item.configurationItem !== undefined) {
                                setInvalidateSingle(item.configurationItem)
                            }
                            if (invalidateSingle !== undefined)
                                handleInvalidate([invalidateSingle], () => setInvalidateSingle(undefined), open, isDocumentUpdatable(item))
                        }
                    }}
                    hidden={!isUserLogged || !canManageOpinionDoc}
                    aria={{ 'aria-haspopup': 'dialog' }}
                />,
                <ButtonLink
                    key={'buttonDelete'}
                    label={t('actionOverTable.options.delete')}
                    disabled={!isUserAdmin}
                    onClick={() => {
                        if (data !== undefined) {
                            const item = data[row.row.index]
                            if (item.configurationItem !== undefined) {
                                setDeleteSingle(item.configurationItem)
                            }
                            if (deleteSingle !== undefined)
                                handleDeleteFile([deleteSingle], () => setDeleteSingle(undefined), open, isDocumentUpdatable(item))
                        }
                    }}
                    inPopup
                    hidden={!isUserLogged || !canManageOpinionDoc}
                    aria={{ 'aria-haspopup': 'dialog' }}
                />,
            ]
        } else {
            return [
                <ButtonLink
                    key={'buttonDownload'}
                    inPopup
                    label={`${t('actionOverTable.options.download')} ${docMeta?.metaComponent ?? ''}`}
                    onClick={() => {
                        const item = data ? data[row.row.index] : {}
                        downloadFile(
                            `${DMS_DOWNLOAD_FILE}${item.configurationItem?.uuid}`,
                            item.configurationItem?.attributes?.Gen_Profil_nazov as string,
                        )
                    }}
                />,
                <ButtonLink
                    key={'buttonUpdate'}
                    label={t('actionOverTable.options.update')}
                    disabled={!isUserLogged}
                    onClick={() => {
                        if (data !== undefined) {
                            const item = data[row.row.index]
                            if (item.configurationItem !== undefined) {
                                setUpdateFile(item.configurationItem)
                            }
                            if (updateFile !== undefined) handleUpdateFile([updateFile], () => setUpdateFile(undefined), open)
                        }
                    }}
                    hidden={!isUserLogged}
                    aria={{ 'aria-haspopup': 'dialog' }}
                    inPopup
                />,
                <ButtonLink
                    key={'buttonInvalidate'}
                    label={t('actionOverTable.options.invalidate')}
                    disabled={!isUserLogged}
                    onClick={() => {
                        if (data !== undefined) {
                            const item = data[row.row.index]
                            if (item.configurationItem !== undefined) {
                                setInvalidateSingle(item.configurationItem)
                            }
                            if (invalidateSingle !== undefined)
                                handleInvalidate([invalidateSingle], () => setInvalidateSingle(undefined), open, isDocumentUpdatable(item))
                        }
                    }}
                    hidden={!isUserLogged}
                    aria={{ 'aria-haspopup': 'dialog' }}
                    inPopup
                />,

                <ButtonLink
                    key={'buttonDelete'}
                    label={t('actionOverTable.options.delete')}
                    disabled={!isUserAdmin}
                    onClick={() => {
                        if (data !== undefined) {
                            const item = data[row.row.index]
                            if (item.configurationItem !== undefined) {
                                setDeleteSingle(item.configurationItem)
                            }
                            if (deleteSingle !== undefined)
                                handleDeleteFile([deleteSingle], () => setDeleteSingle(undefined), open, isDocumentUpdatable(item))
                        }
                    }}
                    hidden={!isUserLogged}
                    aria={{ 'aria-haspopup': 'dialog' }}
                    inPopup
                />,

                <ButtonLink
                    key={'buttonHistory'}
                    label={t('actionOverTable.options.history')}
                    onClick={() => {
                        if (data !== undefined) {
                            const item = data[row.row.index]
                            if (item.configurationItem !== undefined) {
                                setSingleItemHistory(item.configurationItem)
                            }
                        }
                    }}
                    inPopup
                />,
            ]
        }
    }

    const columns: Array<ColumnDef<TableCols>> = [
        ...(isUserLogged
            ? [
                  {
                      accessorFn: (row) => row.selected,
                      header: ({ table }) => (
                          <div className="govuk-checkboxes govuk-checkboxes--small">
                              <CheckBox
                                  checked={table.getIsAllRowsSelected()}
                                  label=""
                                  name="checkbox"
                                  id="checkbox-all"
                                  onChange={table.getToggleAllRowsSelectedHandler()}
                                  aria-label={t('table.selectAllItems')}
                              />
                          </div>
                      ),
                      size: 45,
                      id: CHECKBOX_CELL,
                      cell: ({ row }) => (
                          <div className="govuk-checkboxes govuk-checkboxes--small">
                              <CheckBox
                                  label=""
                                  name="checkbox"
                                  id={`checkbox_${row.id}`}
                                  onChange={row.getToggleSelectedHandler()}
                                  checked={row.getIsSelected()}
                                  aria-label={t('table.selectItem', {
                                      itemName: row.original.configurationItem?.attributes?.Gen_Profil_nazov,
                                  })}
                              />
                          </div>
                      ),
                  } as ColumnDef<TableCols>,
              ]
            : []),
        {
            accessorFn: (row) => row?.configurationItem,
            header: t('documentsTab.table.name'),
            id: 'documentsTab.table.name',
            size: 300,
            meta: {
                getCellContext: (ctx: CellContext<ConfigurationItemUi, unknown>) =>
                    (ctx?.getValue?.() as ConfigurationItemUi).attributes?.Gen_Profil_nazov,
            },
            cell: (row) => {
                const docMeta = filesSizes.find((f) => f.uuid == row.row.original?.configurationItem?.uuid)

                const ci = row.getValue() as ConfigurationItemUi
                if (ci.uuid) {
                    return <a href={`${DMS_DOWNLOAD_FILE}${ci?.uuid}`}>{`${ci?.attributes?.Gen_Profil_nazov} ${docMeta?.metaComponent ?? ''}`}</a>
                } else {
                    return ci?.attributes?.Gen_Profil_nazov
                }
            },
        },
        {
            accessorFn: (row) => row?.configurationItem?.attributes?.Gen_Profil_poznamka,
            header: t('documentsTab.table.note'),
            id: 'documentsTab.table.note',
            size: 200,
            meta: {
                getCellContext: (ctx: CellContext<ConfigurationItemUi, unknown>) => ctx?.getValue?.(),
            },
            cell: (row) => row.getValue() as string,
        },
        {
            accessorFn: (row) => row?.configurationItem?.metaAttributes?.state,
            header: t('documentsTab.table.evidenceStatus'),
            id: 'documentsTab.table.evidenceStatus',
            size: 100,
            meta: {
                getCellContext: (ctx: CellContext<ConfigurationItemUi, unknown>) => t(`metaAttributes.state.${ctx.getValue()}`),
            },
            cell: (row) => (row.getValue() ? (t(`metaAttributes.state.${row.getValue()}`) as string) : ''),
        },
        {
            accessorFn: (row) => row?.configurationItem?.metaAttributes?.createdAt,
            header: t('documentsTab.table.createdAt'),
            id: 'documentsTab.table.createdAt',
            size: 100,
            cell: (ctx) => t('dateTime', { date: ctx.getValue() as string }),
        },
        ...(isUserLogged
            ? [
                  {
                      accessorFn: (row) => row?.configurationItem?.metaAttributes?.createdBy,
                      header: t('documentsTab.table.createdBy'),
                      id: 'documentsTab.table.createdBy',
                      size: 100,
                      cell: (row) => namesData?.find((item) => item.login == (row.getValue() as string))?.fullName,
                  } as ColumnDef<TableCols>,
              ]
            : []),
        {
            accessorFn: (row) => row?.configurationItem?.metaAttributes?.lastModifiedAt,
            header: t('documentsTab.table.lastModifiedAt'),
            id: 'documentsTab.table.lastModifiedAt',
            size: 100,
            cell: (ctx) => t('dateTime', { date: ctx.getValue() as string }),
        },
        ...(isUserLogged
            ? [
                  {
                      accessorFn: (row) => row?.configurationItem?.metaAttributes?.lastModifiedBy,
                      header: t('documentsTab.table.lastModifiedBy'),
                      id: 'documentsTab.table.lastModifiedBy',
                      size: 100,
                      cell: (row) => namesData?.find((item) => item.login == (row.getValue() as string))?.fullName,
                  } as ColumnDef<TableCols>,
              ]
            : []),
        {
            accessorKey: 'bulkActions',
            header: t('documentsTab.table.actions'),
            id: 'bulkActions',
            size: 130,
            cell: (row) => {
                const docMeta = filesSizes.find((f) => f.uuid == row.row.original?.configurationItem?.uuid)
                return <BulkPopup label={t('actionOverTable.options.title')} items={() => resolveBulkActions(row, docMeta)} />
            },
        },
        ...additionalColumnsNullsafe,
    ]

    const protocolsColumns: Array<ColumnDef<ConfigurationItemUi>> = [
        {
            accessorFn: (row) => row,
            header: t('documentsTab.protocols.protocol'),
            id: 'documentsTab.protocols.protocol',
            size: 300,
            cell: (row) => {
                const ci = row.getValue() as ConfigurationItemUi
                return (
                    <Link to={''} onClick={async () => downloadProtocol(ci)}>
                        {ci?.attributes?.Gen_Profil_nazov as string}
                    </Link>
                )
            },
            meta: {
                getCellContext: (ctx: CellContext<ConfigurationItemUi, unknown>) =>
                    (ctx.getValue() as ConfigurationItemUi)?.attributes?.Gen_Profil_nazov,
            },
        },
        {
            accessorFn: (row) => row?.attributes?.Gen_Profil_popis,
            header: t('documentsTab.protocols.description'),
            id: 'documentsTab.protocols.description',
            size: 200,
            meta: {
                getCellContext: (ctx: CellContext<ConfigurationItemUi, unknown>) => ctx.getValue() as string,
            },
        },
        {
            accessorKey: 'bulkActions',
            header: t('documentsTab.table.actions'),
            id: 'bulkActions',
            size: 130,
            cell: (row) => {
                const ci = row.cell.row.original as ConfigurationItemUi

                return <Button label={t('download')} onClick={() => downloadProtocol(ci)} />
            },
        },
    ]

    const { wrapperRef, scrollToMutationFeedback } = useScroll()

    useEffect(() => {
        if (bulkActionResult?.isError || bulkActionResult?.isSuccess) {
            scrollToMutationFeedback()
        }
    }, [bulkActionResult, scrollToMutationFeedback])

    return (
        <QueryFeedback loading={isLoading || isBulkLoading || isLocalLoading} error={isError} indicatorProps={{ layer: 'parent' }} withChildren>
            <div ref={wrapperRef}>
                <MutationFeedback
                    success={bulkActionResult?.isSuccess && bulkActionResult?.additionalInfo?.action !== 'addedDocuments'}
                    successMessage={bulkActionResult?.successMessage + successfullyAdded.join(',')}
                    error={bulkActionResult?.isError && bulkActionResult?.additionalInfo?.action !== 'addedDocuments'}
                    onMessageClose={() => setBulkActionResult(undefined)}
                />
            </div>
            <div ref={wrapperRef}>
                <MutationFeedback
                    success={bulkActionResult?.isSuccess && bulkActionResult?.additionalInfo?.action === 'addedDocuments'}
                    successMessage={t(`addFile${successfullyAdded.length > 1 ? 's' : ''}Success`, {
                        docs: successfullyAdded.join(', '),
                    })}
                    onMessageClose={() => setBulkActionResult(undefined)}
                />
            </div>
            <TextHeading size="L">{t('ciType.documents')}</TextHeading>
            <Filter
                form={() => <></>}
                defaultFilterValues={{ ...defaultFilter, pageSize: Number(currentPreferences.defaultPerPage || defaultFilter.pageSize) }}
                onlySearch
                rowSelection={rowSelection}
            />
            {/* <ButtonLink key={'buttonUpdate'} label={t('actionOverTable.options.updateDocument')} onClick={() => createAndOpenXWikiDoc(false)} />, */}
            <ActionsOverTable
                pagination={pagination}
                handleFilterChange={handleFilterChange}
                entityName={DOCUMENTS}
                hiddenButtons={{ SELECT_COLUMNS: true }}
                createButton={
                    <Button
                        disabled={isInvalidated}
                        label={t('documentsTab.addNewDocument')}
                        onClick={() => setOpenAddModal({})}
                        className={styles.marginBottom0}
                    />
                }
                selectedRowsCount={rowSelection.count}
                bulkPopup={({ selectedRowsCount }) => (
                    <Tooltip
                        descriptionElement={errorMessage}
                        position={'center center'}
                        tooltipContent={(open) => (
                            <BulkPopup
                                checkedRowItems={selectedRowsCount}
                                items={() => [
                                    <ButtonLink
                                        key={'buttonValidateItems'}
                                        label={t('actionOverTable.validateItems')}
                                        disabled={!isUserLogged}
                                        onClick={() => {
                                            handleReInvalidate(
                                                rowSelection.list.flatMap((item) => item.configurationItem ?? {}),
                                                () => setShowReInvalidate(true),
                                                open,
                                            )
                                        }}
                                    />,
                                    <ButtonLink
                                        inPopup
                                        key={'buttonInvalidateItems'}
                                        label={t('actionOverTable.invalidateItems')}
                                        disabled={!isUserLogged}
                                        onClick={() => {
                                            handleInvalidate(
                                                rowSelection.list.flatMap((item) => item.configurationItem ?? {}),
                                                () => setShowInvalidate(true),
                                                open,
                                                isDocumentsUpdatable(rowSelection.list),
                                            )
                                        }}
                                    />,
                                    <ButtonLink
                                        inPopup
                                        key={'buttonDeleteItems'}
                                        label={t('actionOverTable.deleteItems')}
                                        disabled={!isUserAdmin}
                                        onClick={() => {
                                            handleDeleteFile(
                                                rowSelection.list.flatMap((item) => item.configurationItem ?? {}),
                                                () => setShowDeleteFile(true),
                                                open,
                                                isDocumentsUpdatable(rowSelection.list),
                                            )
                                        }}
                                        aria={{ 'aria-haspopup': 'dialog' }}
                                    />,
                                ]}
                            />
                        )}
                    />
                )}
            />
            <InvalidateBulkModal
                items={rowSelection.list.flatMap((item) => item.configurationItem ?? {})}
                open={showInvalidate}
                multiple
                onSubmit={(actionResponse) => handleCloseBulkModal(actionResponse, () => setShowInvalidate(false))}
                onClose={() => setShowInvalidate(false)}
            />
            <ReInvalidateBulkModal
                items={rowSelection.list.flatMap((item) => item.configurationItem ?? {})}
                relations={rowSelection.list.flatMap((item) => item.relationship ?? {})}
                open={showReInvalidate}
                multiple
                onSubmit={(actionResponse) => handleCloseBulkModal(actionResponse, () => setShowInvalidate(false))}
                onClose={() => setShowReInvalidate(false)}
            />
            <DeleteFileBulkModal
                items={rowSelection.list.flatMap((item) => item.configurationItem ?? {})}
                open={showDeleteFile}
                onSubmit={(actionResponse) => handleCloseBulkModal(actionResponse, () => setShowDeleteFile(false))}
                onClose={() => setShowDeleteFile(false)}
            />
            {/* Single items */}
            {invalidateSingle && (
                <InvalidateBulkModal
                    items={[invalidateSingle]}
                    open
                    onSubmit={(actionResponse) => handleCloseBulkModal(actionResponse, () => setInvalidateSingle(undefined))}
                    onClose={() => setInvalidateSingle(undefined)}
                />
            )}
            {deleteSingle && (
                <DeleteFileBulkModal
                    items={[deleteSingle]}
                    open
                    onSubmit={(actionResponse) => handleCloseBulkModal(actionResponse, () => setDeleteSingle(undefined))}
                    onClose={() => setDeleteSingle(undefined)}
                />
            )}
            {updateFile && (
                <UpdateFileModal
                    item={updateFile}
                    open
                    onSubmit={(actionResponse) => handleCloseBulkModal(actionResponse, () => setUpdateFile(undefined))}
                    onClose={() => setUpdateFile(undefined)}
                />
            )}
            {openAddModal && (
                <ProjectUploadFileModal
                    isCi
                    project={ciData}
                    docNumber={String(data?.length) ?? '0'}
                    item={openAddModal}
                    open
                    onClose={() => setOpenAddModal(undefined)}
                    onSubmit={(actionResponse) => handleCloseBulkModal(actionResponse, () => setOpenAddModal(undefined))}
                    setSuccessfullyAdded={setSuccessfullyAdded}
                />
            )}
            {singleItemHistory && <FileHistoryModal item={singleItemHistory} onClose={() => setSingleItemHistory(undefined)} />}
            <Table<TableCols>
                rowSelection={rowSelection.rowSelection}
                onRowSelectionChange={rowSelection.getOnRowSelectionChange({
                    pageNumber: pagination.pageNumber,
                    mapper: (item) => data?.find((d) => d.configurationItem?.uuid === item) ?? {},
                })}
                getRowId={(originalRow) => `${originalRow.configurationItem?.uuid}`}
                columns={columns}
                data={documents}
                key={documents?.length}
            />
            <PaginatorWrapper {...pagination} handlePageChange={handleFilterChange} />
            <Spacer vertical />
            <TextHeading size="L">{t('ciType.protocols')}</TextHeading>
            <Table<ConfigurationItemUi> columns={protocolsColumns} data={protocols} />
        </QueryFeedback>
    )
}
